import { FC, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, ModalProps } from 'reactstrap';

// The modal component props
interface ConfirmModalProps {
   onClose: (confirmed: boolean) => void;
   message?: ReactNode;
   title?: ReactNode;
   confirmText?: ReactNode;
   cancelText?: ReactNode;
   confirmColor?: string;
   cancelColor?: string;
   className?: string;
   buttonsComponent?: FC<{ onClose: (confirmed: boolean) => void }>;
   size?: 'sm' | 'lg' | 'xl';
   bodyComponent?: FC;
   modalProps?: Partial<ModalProps>;
}

const ConfirmModal: FC<ConfirmModalProps> = ({
                                                onClose,
                                                message = 'Are you sure?',
                                                title = 'Warning!',
                                                confirmText = 'Ok',
                                                cancelText = 'Cancel',
                                                confirmColor = 'primary',
                                                cancelColor = '',
                                                className = '',
                                                buttonsComponent: ButtonsComponent,
                                                size,
                                                bodyComponent: BodyComponent,
                                                modalProps = {},
                                             }) => {
   const buttonsContent = ButtonsComponent ? (
      <ButtonsComponent onClose={onClose} />
   ) : (
      <>
         {cancelText && (
            <Button color={cancelColor} onClick={() => onClose(false)}>
               {cancelText}
            </Button>
         )}{' '}
         <Button color={confirmColor} onClick={() => onClose(true)}>
            {confirmText}
         </Button>
      </>
   );

   return (
      <Modal
         size={size}
         isOpen
         toggle={() => onClose(false)}
         className={`reactstrap-confirm ${className}`}
         {...modalProps}
      >
         {title && (
            <ModalHeader toggle={() => onClose(false)}>{title}</ModalHeader>
         )}
         <ModalBody>
            {BodyComponent ? <BodyComponent /> : message}
         </ModalBody>
         <ModalFooter>{buttonsContent}</ModalFooter>
      </Modal>
   );
};

// The options for the confirmAsync function (without onClose)
type ConfirmOptions = Omit<ConfirmModalProps, 'onClose'>;

// The actual function
export default function confirmAsync(options: ConfirmOptions = {}): Promise<boolean> {
   return new Promise((resolve) => {
      const container = document.createElement('div');
      document.body.appendChild(container);

      const root = createRoot(container);

      const handleClose = (result: boolean) => {
         root.unmount();
         document.body.removeChild(container);
         resolve(result);
      };

      // Spread the options and add onClose
      const props: ConfirmModalProps = {
         ...options,
         onClose: handleClose
      };

      root.render(<ConfirmModal {...props} />);
   });
}